import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Box,
  Button,
  ModalHeader,
  ModalFooter,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDown, UsersPlus, XClose } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { useHrisIntegrations } from '@/components/ats/hris/useHrisIntegrations';
import { useSelector } from 'react-redux';
import { usePushToHris } from '@/components/ats/hris/useHrisIntegrations';
import { useState, useRef } from 'react';
import EmployeeFormDrawer from '@/components/ats/hris/EmployeeFormDrawer';
import { CreatePositionDrawer } from '@/components/ats/hris/workdayPosition/CreatePositionDrawer';
import { useWorkdayPositionPush } from '@/components/ats/hris/workdayPosition/useWorkdayPositionPush';
import { useWorkdayHire } from '@/components/ats/hris/workdayHire/useWorkdayHire';
import { WorkdayHireMenu } from '@/components/ats/hris/workdayHire/WorkdayHireMenu';
import { useShowToast } from '@/hooks';

const AddToHRISModal = ({
  modalActions,
  applicationData,
  refetch = () => {},
}) => {
  const { getUnpushedIntegrations, renderIcon } = useHrisIntegrations();
  const { usesWorkdayHire } = useWorkdayHire();
  const toast = useShowToast();
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showPositionForm, setShowPositionForm] = useState(false);
  const employeeDrawerActions = useDisclosure();
  const [formConfig, setFormConfig] = useState([]);
  const [currentIntegration, setCurrentIntegration] = useState(null);
  const [currentAppId, setCurrentAppId] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const formRef = useRef(null);

  const settings = useSelector((state) => state.bootstrap?.settings);
  const activeIntegrations =
    settings?.account?.account_integrations?.hris_integrations || [];

  const pushed = applicationData?.pushed_to_hris || [];

  const unpushedIntegrations = getUnpushedIntegrations(
    activeIntegrations,
    pushed
  );

  const contact = applicationData?.contact || {};
  const fullName = `${contact?.first_name || contact?.name || ''} ${
    contact?.last_name || ''
  }`.trim();

  const { handlePushToHRIS, isPending, isFetchingForm } = usePushToHris({
    onSuccess: () => {
      modalActions?.onClose();
      setShowEmployeeForm(false);
    },
  });

  const workdayPositionPush = useWorkdayPositionPush({
    applicationData,
    onPositionCreated: refetch,
  });

  const handleCancel = () => {
    modalActions?.onClose();
    setShowEmployeeForm(false);
    setShowPositionForm(false);
    employeeDrawerActions.onClose();
    refetch();
  };

  const handleClosePositionForm = () => {
    workdayPositionPush.positionDrawerProps.onClose();
    setShowPositionForm(false);
    modalActions?.onClose();
  };

  const handleSuccess = () => {
    setShowEmployeeForm(false);
    employeeDrawerActions.onClose();
    modalActions?.onClose();
    refetch();
  };

  /**
   * Which Workday form to open, asked before opening it.
   *
   * Only with the hire-form flag on: without it Workday uses Kombo's employee
   * form, which has no notion of a rehire, so there is nothing to choose.
   * Only once a position is ready — before that the offer is the position.
   */
  const offersHireType = (integration) =>
    usesWorkdayHire(integration?.type) &&
    !workdayPositionPush.getActionLabel(integration);

  const handlePush = async (integration, { hireType = 'new_hire' } = {}) => {
    // Workday needs a position before it will accept an employee: while none is
    // ready this opens the position form instead of the employee form.
    //
    // The modal is hidden, not closed: every caller renders this component only
    // while `modalActions.isOpen`, so closing it here would unmount the position
    // drawer along with it. Same reason `showEmployeeForm` exists.
    const intercepted = workdayPositionPush.interceptPush(integration);
    if (intercepted) {
      // Only hide the modal once the create drawer is actually behind it —
      // a syncing position intercepts but opens nothing, and hiding there
      // would leave an empty screen. `positionDrawerProps.isOpen` cannot
      // answer this: it still holds the pre-update value in this render.
      setShowPositionForm(intercepted === 'opened');
      return;
    }

    await handlePushToHRIS(integration, applicationData, {
      hireType,
      formRef,
      onFormReady: ({ formSchema, mappedValues, rehireOnly }) => {
        // Workday already has this person as an employee, so the new-hire form
        // we just asked for would create a duplicate. Fetch the rehire form
        // instead of opening the wrong one — there is no toggle to correct it
        // with, and the recruiter has no way to know.
        if (rehireOnly && hireType === 'new_hire') {
          toast({
            title: `${fullName || 'This candidate'} is already an employee in ${integration?.name || 'Workday'}, so this is a rehire.`,
            status: 'info',
          });
          void handlePush(integration, { hireType: 'rehire' });
          return;
        }
        setInitialValues(mappedValues);
        setFormConfig(formSchema);
        setCurrentIntegration(integration);
        setCurrentAppId(applicationData?.id);
        setShowEmployeeForm(true);
        employeeDrawerActions.onOpen();
      },
      onDirectSuccess: () => {
        modalActions?.onClose();
        refetch();
      },
    });
  };

  const getLoadingText = (integration) => {
    return isFetchingForm
      ? 'Loading employee form'
      : `Adding to ${integration.name}`;
  };

  const isBusy = isFetchingForm || isPending;

  const renderContent = () => {
    if (unpushedIntegrations.length === 1) {
      const integration = unpushedIntegrations[0];
      // Workday with no ready position: the offer is the position, not the hire.
      const positionLabel = workdayPositionPush.getActionLabel(integration);
      const isPositionSyncing = workdayPositionPush.isActionDisabled(
        integration?.type
      );
      return (
        <>
          <ModalHeader>
            <Flex gap="6px" alignItems="center">
              {renderIcon(integration)}
              <KText
                color="secondary-900"
                size="md-bold"
                wordBreak="break-word"
              >
                {positionLabel || `Add ${fullName} to ${integration.name}`}
              </KText>
            </Flex>
          </ModalHeader>
          <ModalBody py="0">
            <KText>
              {positionLabel
                ? `${integration.name} needs an open position before ${fullName} can be hired into it.`
                : offersHireType(integration)
                  ? `Has ${fullName} worked here before? A rehire uses the record ${integration.name} already holds.`
                  : `Create ${fullName} as a new employee on ${integration.name}.`}
            </KText>
          </ModalBody>
          <ModalFooter>
            <Flex gap="6px" flexDir="row-reverse">
              {offersHireType(integration) ? (
                // One control, not two buttons: the recruiter is choosing how
                // to do one action, and this matches the "Add to HRIS" menu.
                <WorkdayHireMenu
                  onSelect={(hireType) => handlePush(integration, { hireType })}
                  trigger={
                    <Button
                      size="sm"
                      variant="primary"
                      isLoading={isBusy}
                      loadingText={getLoadingText(integration)}
                      rightIcon={<ChevronDown size={16} />}
                    >
                      {`Add to ${integration.name}`}
                    </Button>
                  }
                />
              ) : (
                <Button
                  size="sm"
                  variant="primary"
                  isLoading={isFetchingForm || isPending}
                  loadingText={getLoadingText(integration)}
                  isDisabled={isPositionSyncing}
                  onClick={() => handlePush(integration)}
                >
                  {positionLabel || `Add to ${integration.name}`}
                </Button>
              )}
              <Button size="sm" variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
            </Flex>
          </ModalFooter>
        </>
      );
    } else {
      return (
        <>
          <ModalHeader>
            <Flex gap="6px" alignItems="center">
              <UsersPlus size={20} className="kula-icon" />
              <KText
                color="secondary-900"
                size="md-bold"
                wordBreak="break-word"
              >
                Add {fullName} to HRIS
              </KText>
            </Flex>
          </ModalHeader>
          <ModalBody py="0">
            <KText>Create {fullName} to your HRIS as a new employee</KText>
          </ModalBody>
          <ModalFooter>
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant="primary"
                isLoading={isFetchingForm || isPending}
                loadingText={
                  isFetchingForm
                    ? 'Loading employee form'
                    : `Adding to ${currentIntegration?.name}`
                }
                rightIcon={<ChevronDown size={16} />}
              >
                Add to HRIS
              </MenuButton>
              <MenuList zIndex={9}>
                {/* Workday contributes two rows once a position is ready —
                    the two forms differ in shape, so the choice belongs here
                    rather than inside the form. Every other integration, and
                    Workday with the hire-form flag off, contributes one. */}
                {unpushedIntegrations.flatMap((integration) =>
                  (offersHireType(integration)
                    ? [
                        { hireType: 'new_hire', suffix: ' — new hire' },
                        { hireType: 'rehire', suffix: ' — rehire' },
                        { hireType: 'conversion', suffix: ' — conversion' },
                      ]
                    : [{ hireType: 'new_hire', suffix: '' }]
                  ).map(({ hireType, suffix }) => (
                    <MenuItem
                      key={`${integration.type}${suffix}`}
                      isDisabled={workdayPositionPush.isActionDisabled(
                        integration?.type
                      )}
                      onClick={() => {
                        setCurrentIntegration(integration);
                        handlePush(integration, { hireType });
                      }}
                    >
                      <Flex gap="8px" alignItems="center">
                        {renderIcon(integration, {
                          width: '16px',
                          height: '16px',
                        })}
                        <KText>
                          {workdayPositionPush.getActionLabel(integration) ||
                            `Add to ${integration.name}${suffix}`}
                        </KText>
                      </Flex>
                    </MenuItem>
                  ))
                )}
              </MenuList>
            </Menu>
            <Button
              size="sm"
              variant="secondary"
              ml="8px"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </ModalFooter>
        </>
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={modalActions?.isOpen && !showEmployeeForm && !showPositionForm}
        onClose={modalActions?.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <Box
            position="absolute"
            right="10px"
            top="10px"
            cursor="pointer"
            onClick={handleCancel}
          >
            <XClose
              size={20}
              color="var(--kula-colors-secondary-800)"
              className="kula-icon"
            />
          </Box>
          {renderContent()}
        </ModalContent>
      </Modal>
      {/* Gated on the element, as in ApplicationActions: the drawer's
          `!isOpen` guard sits after its hooks. */}
      {workdayPositionPush.positionDrawerProps.isOpen && (
        <CreatePositionDrawer
          {...workdayPositionPush.positionDrawerProps}
          onClose={handleClosePositionForm}
        />
      )}
      <EmployeeFormDrawer
        isOpen={employeeDrawerActions.isOpen && showEmployeeForm}
        onClose={() => {
          employeeDrawerActions.onClose();
          setShowEmployeeForm(false);
          modalActions?.onClose();
          refetch();
        }}
        integrationName={currentIntegration?.name || ''}
        integration={currentIntegration}
        applicationId={currentAppId}
        formConfig={formConfig}
        onSuccess={handleSuccess}
        formRef={formRef}
        initialValues={initialValues}
      />
    </>
  );
};

export default AddToHRISModal;
